import * as React from 'react';

import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormHelperText from '@mui/material/FormHelperText';
import OutlinedInput from '@mui/material/OutlinedInput';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import {
  black,
  blackText,
  blue,
  gray,
  grayInput,
  primarycolor
} from '../../constants/Colors';
import {
  formLabelWrapper,
  formNarrowMb,
  formWideMb,
  titleMb
} from '../../constants/Location';

import type { SxProps } from '@mui/material';
import type { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';

type OutlinedInputProps = React.ComponentProps<typeof OutlinedInput>;

type Props = OutlinedInputProps & {
  labelText?: string;
  requiredText?: string;
  isRequired: boolean;
  error?: boolean;
  helperText?:
    | string
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>
    | undefined;
  fieldType?: string;
  width?: string;
  spWidth?: string;
  formMb?: string | undefined;
  guideText?: string;
  underText?: string;
  underTextColor?: string;
  disabledColor?: string;
  otherError?: string | null;
};

const CustomTextfield = React.forwardRef((props: Props): React.ReactElement => {
  const { t } = useTranslation();
  const {
    labelText,
    requiredText,
    isRequired,
    error,
    helperText,
    formMb,
    fieldType,
    width,
    guideText,
    underText,
    underTextColor,
    disabledColor,
    otherError,
    ...rest
  } = props;

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  function inputBorderFocusColor(): string {
    if (error !== false ? error : Boolean(otherError)) {
      return primarycolor;
    }
    return black;
  }

  function inputBorderColor(): string {
    if (error !== false ? error : Boolean(otherError)) {
      return primarycolor;
    }
    return grayInput;
  }

  let inputHeight;
  if (fieldType === 'textarea') {
    inputHeight = '128px';
  } else if (fieldType === 'textareaRow10') {
    inputHeight = '280px';
  } else if (fieldType === 'textareaRow9') {
    inputHeight = '250px';
  } else if (fieldType === 'textareaRow8') {
    inputHeight = '220px';
  } else if (fieldType === 'textareaRow7') {
    inputHeight = '190px';
  } else if (fieldType === 'textareaRow6') {
    inputHeight = '160px';
  } else if (fieldType === 'textareaRow5') {
    inputHeight = '140px';
  } else if (fieldType === 'textareaRow4') {
    inputHeight = '120px';
  } else if (fieldType === 'textareaRow3') {
    inputHeight = '88px';
  } else if (fieldType === 'textareaRow2') {
    inputHeight = '64px';
  } else {
    inputHeight = '40px';
  }

  let inputWidth;
  let inputSpWidth;
  if (width === 'narrow') {
    inputWidth = '180px';
    inputSpWidth = '148px';
  } else if (width === 'other') {
    inputWidth = '358px';
    inputSpWidth = '278px';
  } else if (width === 'search') {
    inputWidth = '120px';
    inputSpWidth = '120px';
  } else if (width === 'article') {
    inputWidth = '100%';
    inputSpWidth = '100%';
  } else if (width === 'contactDrawer') {
    inputWidth = '100%';
    inputSpWidth = '100%';
  } else if (width === 'half') {
    inputWidth = '50%';
    inputSpWidth = '50%';
  } else if (width === 'message') {
    inputWidth = '340px';
    inputSpWidth = '80%';
  } else {
    inputWidth = '400px';
    inputSpWidth = '311px';
  }

  const styles = {
    formLabelWrapper,
    guideCard: {
      '@media screen and (max-width:600px)': {
        width: '80%'
      },
      width: '480px'
    },
    guideCloseButton: {
      color: blue
    },
    guideMark: {
      color: gray,
      cursor: 'pointer',
      heihgt: '18px',
      marginLeft: '8px',
      verticalAlign: 'middle',
      width: '18px'
    },
    guideText: {
      color: blackText,
      fontSize: '14px',
      whiteSpace: 'pre-wrap'
    },
    guideTitle: {
      color: blackText,
      fontSize: '22px',
      marginBottom: titleMb,
      padding: 0
    },
    helper: {
      '@media screen and (max-width:480px)': {
        marginLeft: 0
      }
    },
    input: {
      '& .MuiInputBase-input.Mui-disabled': {
        WebkitTextFillColor: disabledColor ?? 'initial'
      },
      '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
        borderColor: inputBorderColor()
      },
      '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: inputBorderFocusColor()
      },
      '&.MuiOutlinedInput-root:hover': {
        borderColor: inputBorderFocusColor()
      },
      '@media screen and (max-width:480px)': {
        width: inputSpWidth
      },
      height: inputHeight,
      width: inputWidth
    },
    label: {
      fontSize: '12px'
    },
    required: {
      color: primarycolor,
      fontSize: '12px',
      marginLeft: '10px'
    },
    underText: {
      color: gray,
      ml: '14px'
    }
  };

  const inputformMb = (marginBottom: string | undefined): SxProps =>
    marginBottom === 'narrow' ? formNarrowMb : formWideMb;

  const textfieldHelper = (): string | undefined => {
    if (helperText === undefined) return;
    return String(helperText);
  };

  return (
    <Typography sx={inputformMb(formMb)} align="left" component="div">
      <Typography align="left" sx={styles.formLabelWrapper} component="div">
        <Typography color={gray} sx={styles.label} component="span">
          {labelText}
        </Typography>
        {isRequired ? (
          <Typography component="span" sx={styles.required}>
            {requiredText}
          </Typography>
        ) : (
          ''
        )}
        {guideText ? (
          <Typography component="span" sx={{ marginTop: '10px' }}>
            <HelpOutlineOutlinedIcon
              sx={styles.guideMark}
              onClick={handleClickOpen}
            />
            <Dialog open={open} onClose={handleClose}>
              <DialogContent sx={styles.guideCard}>
                <DialogTitle sx={styles.guideTitle}>
                  {labelText}
                  {t('form.guideTitle')}
                </DialogTitle>
                <DialogContentText sx={styles.guideText}>
                  {guideText}
                </DialogContentText>
                <DialogActions>
                  <Button onClick={handleClose} sx={styles.guideCloseButton}>
                    {t('form.guideClose')}
                  </Button>
                </DialogActions>
              </DialogContent>
            </Dialog>
          </Typography>
        ) : (
          ''
        )}
      </Typography>
      <OutlinedInput {...rest} value={rest.value ?? ''} sx={styles.input} />
      <FormHelperText sx={styles.helper} error={error}>
        {error && textfieldHelper()}
      </FormHelperText>
      <FormHelperText sx={styles.helper} error={Boolean(otherError)}>
        {otherError}
      </FormHelperText>
      {underText && (
        <Typography
          component="p"
          sx={[styles.label, styles.underText, { color: underTextColor }]}
        >
          {underText}
        </Typography>
      )}
    </Typography>
  );
});
CustomTextfield.displayName = 'CustomTextfield';
export default CustomTextfield;
